// v8.9.7 — Operator console.
//
// Admin-gated mirror of /trade that exposes all technical / diagnostic
// panels (Trail Config, BRR Gate Trace, Market Condition, Risk Profile,
// BRR Forced Symbols, Cold Pairs, Filter Stats, Block Reasons, raw
// Activity Log). Users see none of this — they only get the clean
// /trade view via the default `operatorMode={false}` prop on CloudBotPanel.
//
// Reuses the existing `/api/admin/login` JWT (same auth as
// /partner) so we don't introduce a second admin system.
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowLeft, LogOut, ShieldCheck, Mail, KeyRound, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/marketing/Logo";
import CloudBotPanel from "@/components/dashboard/CloudBotPanel";
import AdminInsights from "@/components/dashboard/AdminInsights";
import { useAuth } from "@/hooks/useAuth";
import { adminLogin, adminMe, clearAdminToken, getAdminToken } from "@/lib/adminAuth";

const OperatorConsole = () => {
  const navigate = useNavigate();
  const { session, displayBalance } = useAuth();
  const [checking, setChecking] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getAdminToken()) {
        if (!cancelled) setChecking(false);
        return;
      }
      const me = await adminMe();
      if (cancelled) return;
      setAdmin(me);
      setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogin = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    setError("");
    try {
      const data = await adminLogin(email.trim(), password);
      setAdmin({ email: data.email, role: "admin" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid credentials. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    clearAdminToken();
    setAdmin(null);
    setEmail("");
    setPassword("");
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-vts-bg">
        <div className="text-vts-silver/55 text-sm font-mono">Checking operator session…</div>
      </div>
    );
  }

  // -------- LOGIN GATE --------
  if (!admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-vts-bg px-4">
        <div className="w-full max-w-md vts-card p-6 lg:p-8" data-testid="operator-login-card">
          <div className="flex items-center gap-2 mb-6">
            <Logo />
            <span className="text-[10px] uppercase tracking-widest text-vts-silver/55 font-mono">
              Operator Console
            </span>
          </div>
          <h1 className="font-heading text-2xl text-white font-bold mb-2 flex items-center gap-2">
            <Lock className="h-5 w-5 text-vts-blue" />
            Restricted area
          </h1>
          <p className="text-[12px] text-vts-silver/70 font-mono leading-relaxed mb-6">
            This page is reserved for trading-desk operators. Diagnostic tools and
            tuning controls live here — never visible to traders.
          </p>
          <form onSubmit={handleLogin} className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-vts-silver/65 font-mono">
                <Mail className="inline h-3 w-3 mr-1" />
                Operator email
              </Label>
              <Input
                data-testid="operator-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 bg-vts-surface border-vts-line text-white font-mono"
                placeholder="ops@vts.local"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-vts-silver/65 font-mono">
                <KeyRound className="inline h-3 w-3 mr-1" />
                Password
              </Label>
              <Input
                data-testid="operator-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 bg-vts-surface border-vts-line text-white font-mono"
              />
            </div>
            {error && (
              <div className="text-[11px] font-mono text-vts-loss flex items-center gap-1.5">
                <AlertTriangle className="h-3 w-3" />
                {error}
              </div>
            )}
            <Button
              type="submit"
              data-testid="operator-submit"
              disabled={submitting}
              className="w-full bg-vts-blue hover:bg-vts-blue/85 text-black font-semibold"
            >
              {submitting ? "Verifying…" : "Sign in"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate("/")}
              className="w-full text-vts-silver/55 hover:text-white text-[11px] font-mono"
            >
              <ArrowLeft className="h-3 w-3 mr-1" />
              Back to public site
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // -------- AUTHED CONSOLE --------
  if (!session?.account_id) {
    return (
      <div className="min-h-screen bg-vts-bg px-4 py-10">
        <div className="max-w-7xl mx-auto space-y-5">
          <div className="flex flex-col items-center text-center gap-3">
            <ShieldCheck className="h-10 w-10 text-vts-blue" />
            <div>
              <h1 className="font-heading text-xl text-white font-bold mb-1">
                Operator signed in · no trader account attached
              </h1>
              <p className="text-[12px] text-vts-silver/70 font-mono max-w-md leading-relaxed">
                Bot diagnostics need a Deriv account connected at <code>/trade</code>.
                Admin data below works without one.
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => navigate("/trade")} className="bg-vts-blue hover:bg-vts-blue/85 text-black">
                Go to /trade
              </Button>
              <Button variant="ghost" onClick={handleLogout} className="text-vts-silver/65 hover:text-white">
                <LogOut className="h-3 w-3 mr-1" /> Sign out
              </Button>
            </div>
          </div>
          <AdminInsights />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vts-bg">
      <header className="border-b border-vts-line bg-vts-surface/40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="text-[10px] uppercase tracking-widest text-vts-blue font-mono bg-vts-blue/10 border border-vts-blue/30 px-2 py-0.5 rounded">
              Operator console
            </span>
          </div>
          <div className="flex items-center gap-2 text-[11px] font-mono">
            <span className="text-vts-silver/55">{admin.email}</span>
            <Button variant="ghost" size="sm" onClick={() => navigate("/trade")} className="text-vts-silver/70 hover:text-white">
              <ArrowLeft className="h-3 w-3 mr-1" />
              Trader view
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-vts-silver/70 hover:text-white">
              <LogOut className="h-3 w-3 mr-1" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <div className="vts-card p-4 lg:p-5 bg-vts-blue/5 border-vts-blue/30">
          <div className="flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 text-vts-blue mt-0.5 shrink-0" />
            <div>
              <div className="text-[13px] text-white font-semibold mb-0.5">Diagnostic mode active</div>
              <p className="text-[11px] text-vts-silver/70 font-mono leading-relaxed">
                Trail Config · BRR Gate Trace · Market Condition · Risk Profile · BRR Forced Symbols ·
                Cold Pairs · Filter Stats · Block Reasons · raw activity log. Traders never see these.
              </p>
            </div>
          </div>
        </div>

        <AdminInsights />
        <CloudBotPanel
          balance={displayBalance?.balance}
          currency={displayBalance?.currency || "USD"}
          operatorMode={true}
        />
      </main>
    </div>
  );
};

export default OperatorConsole;
